/**
 * Converts help page content to a Word document (.docx).
 *
 * Walks the DOM for elements with `data-help-type` attributes and `<img>` tags,
 * then builds a structured Word document preserving headings, steps, bullets,
 * images, links, alerts, and captioned image tables.
 */

type HelpType = 'title' | 'subheading' | 'body' | 'step' | 'bullet-0' | 'bullet-1' | 'captioned-image' | 'alert';
type ImageData = { buffer: ArrayBuffer; width: number; height: number };

const MAX_IMAGE_WIDTH = 600;
const CAPTIONED_IMAGE_WIDTH = 300;
const NUMBERING_REF = 'help-steps';
const FONT_SIZE = 22;

function collectElements(container: HTMLElement): HTMLElement[] {
    const elements: HTMLElement[] = [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT);
    let node = walker.nextNode() as HTMLElement | null;
    while (node) {
        if (node.hasAttribute('data-help-type') || node.tagName === 'IMG') {
            elements.push(node);
        }
        node = walker.nextNode() as HTMLElement | null;
    }
    return elements;
}

function getImageDimensions(el: HTMLImageElement, natural: ImageData): { width: number; height: number } {
    if (el.offsetWidth > 0) return { width: el.offsetWidth, height: el.offsetHeight };
    return { width: natural.width, height: natural.height };
}

async function fetchImage(src: string): Promise<ImageData | null> {
    try {
        const buffer = await (await fetch(src)).arrayBuffer();
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject();
            img.src = src;
        });
        return { buffer, width: img.naturalWidth, height: img.naturalHeight };
    } catch {
        return null;
    }
}

async function fetchAllImages(elements: HTMLElement[]): Promise<Map<HTMLElement, ImageData>> {
    const imageMap = new Map<HTMLElement, ImageData>();
    const imageNodes = elements.filter((el) => el.tagName === 'IMG');
    const results = await Promise.all(imageNodes.map((el) => fetchImage((el as HTMLImageElement).src)));
    imageNodes.forEach((el, index) => {
        const data = results[index];
        if (data) {
            const { width, height } = getImageDimensions(el as HTMLImageElement, data);
            imageMap.set(el, { buffer: data.buffer, width, height });
        }
    });
    return imageMap;
}

function scaleToFit(width: number, height: number, maxWidth: number) {
    if (width <= maxWidth) return { width, height };
    return { width: maxWidth, height: Math.round(height * (maxWidth / width)) };
}

export async function helpToDocx(container: HTMLElement): Promise<Blob> {
    const {
        AlignmentType,
        BorderStyle,
        Document,
        ExternalHyperlink,
        HeadingLevel,
        ImageRun,
        LevelFormat,
        Packer,
        Paragraph,
        ShadingType,
        Table,
        TableCell,
        TableRow,
        TextRun,
        WidthType,
        VerticalAlign,
    } = await import('docx');

    function text(content: string, opts?: { bold?: boolean; italics?: boolean }) {
        return new TextRun({ text: content, size: FONT_SIZE, bold: opts?.bold, italics: opts?.italics });
    }

    function inlineChildren(el: HTMLElement) {
        const hasInlineMarkup = el.querySelector('a[href], b, strong, em, i');
        if (!hasInlineMarkup) return [text(el.textContent?.trim() || '')];
        const children: any[] = [];
        const walk = (node: Node, opts?: { bold?: boolean; italics?: boolean }) => {
            if (node.nodeType === Node.TEXT_NODE) {
                const t = (node.textContent || '').replace(/\s+/g, ' ');
                if (t) children.push(text(t, opts));
            } else if (node.nodeName === 'A') {
                const anchor = node as HTMLAnchorElement;
                children.push(
                    new ExternalHyperlink({
                        link: anchor.href,
                        children: [
                            new TextRun({
                                text: anchor.textContent || '',
                                style: 'Hyperlink',
                                size: FONT_SIZE,
                                bold: opts?.bold,
                                italics: opts?.italics,
                            }),
                        ],
                    })
                );
            } else if (node.nodeName === 'B' || node.nodeName === 'STRONG') {
                node.childNodes.forEach((child) => walk(child, { ...opts, bold: true }));
            } else if (node.nodeName === 'EM' || node.nodeName === 'I') {
                node.childNodes.forEach((child) => walk(child, { ...opts, italics: true }));
            } else {
                node.childNodes.forEach((child) => walk(child, opts));
            }
        };
        el.childNodes.forEach((child) => walk(child));
        return children;
    }

    function imageParagraph(data: ImageData, maxWidth: number) {
        return new Paragraph({
            children: [
                new ImageRun({
                    data: data.buffer,
                    transformation: scaleToFit(data.width, data.height, maxWidth),
                    type: 'png',
                }),
            ],
            spacing: { after: 120 },
        });
    }

    function buildCaptionedTable(captionEl: HTMLElement, imageMap: Map<HTMLElement, ImageData>) {
        // Find the last <img> inside the container — that's the photo
        const imgs = Array.from(captionEl.querySelectorAll('img'));
        const photo = imgs.pop();
        const photoData = photo ? imageMap.get(photo) : null;
        if (!photoData) return null;

        // Build caption content from child data-help-type elements
        const captionChildren = Array.from(captionEl.querySelectorAll<HTMLElement>('[data-help-type]'));
        const captionParagraphs = captionChildren
            .map((child) => elementToParagraph(child))
            .filter((p): p is InstanceType<typeof Paragraph> => p !== null);

        // Include any <img> tags that aren't the photo (e.g. ✓/✗ icons) as small images
        imgs.forEach((img) => {
            const data = imageMap.get(img);
            if (data) captionParagraphs.unshift(imageParagraph(data, data.width));
        });

        if (captionParagraphs.length === 0) {
            captionParagraphs.push(new Paragraph({ children: [text(captionEl.textContent?.trim() || '')] }));
        }

        const scaled = scaleToFit(photoData.width, photoData.height, CAPTIONED_IMAGE_WIDTH);
        const noBorders = {
            top: { style: BorderStyle.NONE, size: 0 },
            bottom: { style: BorderStyle.NONE, size: 0 },
            left: { style: BorderStyle.NONE, size: 0 },
            right: { style: BorderStyle.NONE, size: 0 },
            insideHorizontal: { style: BorderStyle.NONE, size: 0 },
            insideVertical: { style: BorderStyle.NONE, size: 0 },
        };
        return new Table({
            borders: noBorders,
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            children: captionParagraphs,
                            width: { size: 30, type: WidthType.PERCENTAGE },
                            verticalAlign: VerticalAlign.CENTER,
                        }),
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new ImageRun({ data: photoData.buffer, transformation: scaled, type: 'png' }),
                                    ],
                                }),
                            ],
                            width: { size: 70, type: WidthType.PERCENTAGE },
                        }),
                    ],
                }),
            ],
        });
    }

    const customBulletMarkers = new Map<string, string>();
    const stepGroups = new Set<string>();

    function elementToParagraph(el: HTMLElement) {
        const helpType = el.getAttribute('data-help-type') as HelpType;
        const content = el.textContent?.trim() || '';

        switch (helpType) {
            case 'title':
                return new Paragraph({
                    children: [text(content, { bold: true })],
                    heading: HeadingLevel.HEADING_1,
                    alignment: AlignmentType.CENTER,
                    spacing: { after: 200 },
                });
            case 'subheading':
                return new Paragraph({
                    children: [text(content, { bold: true })],
                    heading: HeadingLevel.HEADING_2,
                    spacing: { before: 200, after: 120 },
                });
            case 'body':
                return new Paragraph({ children: inlineChildren(el), spacing: { after: 120 } });
            case 'step': {
                const group = el.getAttribute('data-help-step-group');
                const ref = group ? `${NUMBERING_REF}-${group}` : NUMBERING_REF;
                if (group) stepGroups.add(group);
                return new Paragraph({
                    children: inlineChildren(el),
                    numbering: { reference: ref, level: 0 },
                    spacing: { after: 120 },
                });
            }
            case 'bullet-0':
            case 'bullet-1': {
                const level = helpType === 'bullet-0' ? 0 : 1;
                const marker = el.getAttribute('data-help-marker');
                if (marker) {
                    const ref = `custom-bullet-${marker}`;
                    customBulletMarkers.set(ref, marker);
                    return new Paragraph({
                        children: inlineChildren(el),
                        numbering: { reference: ref, level },
                        spacing: { after: 80 },
                    });
                }
                return new Paragraph({ children: inlineChildren(el), bullet: { level }, spacing: { after: 80 } });
            }
            case 'alert':
                return new Paragraph({
                    children: [text(content, { italics: true })],
                    border: { left: { style: BorderStyle.SINGLE, size: 6, color: '0073bb' } },
                    shading: { type: ShadingType.CLEAR, fill: 'f0f8ff' },
                    indent: { left: 200 },
                    spacing: { before: 120, after: 120 },
                });
            default:
                return null;
        }
    }

    const elements = collectElements(container);
    const imageMap = await fetchAllImages(elements);

    // Track elements inside captioned-image containers so they aren't emitted twice
    const insideCaptioned = new Set<HTMLElement>();
    elements.forEach((el) => {
        if (el.getAttribute('data-help-type') === 'captioned-image') {
            el.querySelectorAll<HTMLElement>('[data-help-type], img').forEach((child) => insideCaptioned.add(child));
        }
    });

    const children: (InstanceType<typeof Paragraph> | InstanceType<typeof Table>)[] = [];

    elements.forEach((el) => {
        if (insideCaptioned.has(el)) return;

        if (el.tagName === 'IMG') {
            const data = imageMap.get(el);
            if (data) children.push(imageParagraph(data, MAX_IMAGE_WIDTH));
            return;
        }

        if (el.getAttribute('data-help-type') === 'captioned-image') {
            const table = buildCaptionedTable(el, imageMap);
            if (table) children.push(table);
            return;
        }

        const paragraph = elementToParagraph(el);
        if (paragraph) children.push(paragraph);
    });

    const bulletLevel = (level: 0 | 1, marker: string) => ({
        level,
        format: LevelFormat.BULLET,
        text: marker,
        alignment: AlignmentType.START,
        style: { paragraph: { indent: { left: level === 0 ? 240 : 480, hanging: 240 } } },
    });
    const customBulletConfigs = Array.from(customBulletMarkers.entries()).map(([ref, marker]) => ({
        reference: ref,
        levels: [bulletLevel(0, marker), bulletLevel(1, marker)],
    }));

    const stepIndent = { paragraph: { indent: { left: 240, hanging: 240 } } };
    const stepLevel = {
        level: 0,
        format: LevelFormat.DECIMAL,
        text: '%1.',
        alignment: AlignmentType.START,
        style: stepIndent,
    };
    const stepConfigs = [
        NUMBERING_REF,
        ...Array.from(stepGroups).map((g) => `${NUMBERING_REF}-${g}`),
    ].map((reference) => ({ reference, levels: [stepLevel] }));

    // Generate document
    return Packer.toBlob(
        new Document({
            numbering: { config: [...stepConfigs, ...customBulletConfigs] },
            sections: [{ children }],
        })
    );
}
